import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
const FLUSH_INTERVAL_MS = 30_000;
const MAX_BATCH_SIZE = 50;

function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

class ActivityTracker {
  constructor() {
    this.queue = [];
    this.sessionId = sessionStorage.getItem('activity_session_id') || generateSessionId();
    sessionStorage.setItem('activity_session_id', this.sessionId);
    this.authToken = null;
    this.currentPage = null;
    this.pageEnteredAt = null;
    this.flushTimer = null;
    this._startFlushing();
    this._setupBeforeUnload();
  }

  setAuthToken(token) {
    this.authToken = token;
  }

  // ── Page views & duration ──────────────────────────────────────────────

  trackPageView(page) {
    this._recordPageDuration();
    this.currentPage = page;
    this.pageEnteredAt = Date.now();
    this._push({ event_type: 'page_view', page });
  }

  // ── Click / interaction events ─────────────────────────────────────────

  trackClick(target, page, metadata) {
    this._push({ event_type: 'click', page: page || this.currentPage, target, metadata });
  }

  trackFeatureUse(feature, page, metadata) {
    this._push({ event_type: 'feature_use', page: page || this.currentPage, target: feature, metadata });
  }

  // ── Internal helpers ───────────────────────────────────────────────────

  _push(partial) {
    this.queue.push({
      session_id: this.sessionId,
      ...partial,
      timestamp: new Date().toISOString(),
    });
    if (this.queue.length >= MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  _recordPageDuration() {
    if (this.currentPage && this.pageEnteredAt) {
      const duration_ms = Date.now() - this.pageEnteredAt;
      if (duration_ms > 500) {
        this.queue.push({
          session_id: this.sessionId,
          event_type: 'page_duration',
          page: this.currentPage,
          duration_ms,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }

  _startFlushing() {
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  _setupBeforeUnload() {
    window.addEventListener('beforeunload', () => {
      this._recordPageDuration();
      this.flush(true);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this._recordPageDuration();
        this.flush(true);
      }
    });
  }

  flush(useBeacon = false) {
    if (this.queue.length === 0) return;

    const events = this.queue.splice(0, MAX_BATCH_SIZE);
    const body = JSON.stringify({ events });

    if (useBeacon && navigator.sendBeacon) {
      const headers = { type: 'application/json' };
      const blob = new Blob([body], headers);
      navigator.sendBeacon(`${API_URL}/activity/track`, blob);
      return;
    }

    const headers = { 'Content-Type': 'application/json' };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    axios.post(`${API_URL}/activity/track`, body, { headers }).catch(() => {});
  }

  destroy() {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this._recordPageDuration();
    this.flush(true);
  }
}

const tracker = new ActivityTracker();
export default tracker;
